import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserConfigDto } from "lib/ShiOneClient";
import {
  deleteUserConfig,
  getUserConfigs,
  postUserConfig,
} from "@api/userSettingsStore";
import { useAuth0 } from "@auth0/auth0-react";
import ms from "ms";

export const cacheKey = "UserConfigs";

export interface UserConfigResponse {
  userConfig: UserConfigDto | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

const useGetUserConfigs = (settingKeys: string) => {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: [cacheKey] as const,
    queryFn: async () => {
      return await getUserConfigs();
    },
    staleTime: ms("5m"),
    enabled: !!settingKeys && isAuthenticated,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
};

export const useGetUserConfig = (settingsKey: string): UserConfigResponse => {
  const { data: userConfigs, error, isLoading, isFetching } = useGetUserConfigs(settingsKey);
  const userConfig = userConfigs?.find(
    (config) => config.settingsKey === settingsKey
  );
  return {
    userConfig,
    isLoading: isLoading && !!settingsKey,
    isFetching: isFetching && !!settingsKey,
    error: (error as Error) || null,
  };
};


export const useUpsertUserConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UserConfigDto>({
    mutationFn: postUserConfig,
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        [cacheKey] as const,
        (oldData: UserConfigDto[] | undefined) => {
          if (!oldData) {
            return [variables];
          }
          const existing = oldData.find(
            (item) => item.settingsKey === variables.settingsKey
          );
          const newData = existing
            ? oldData.map((item) =>
                item.settingsKey === variables.settingsKey ? variables : item
              )
            : [...oldData, variables];
          return newData;
        }
      );
      queryClient.invalidateQueries({
        queryKey: [cacheKey] as const,
      });
    },
  });
};

export const useDeleteUserConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UserConfigDto>({
    mutationFn: deleteUserConfig,
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        [cacheKey] as const,
        (oldData: UserConfigDto[] | undefined) =>
          oldData?.filter((item) => item.settingsKey !== variables.settingsKey) ??
          []
      );
      queryClient.invalidateQueries({
        queryKey: [cacheKey] as const,
      });
    },
  });
};
